import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [studentName, setStudentName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [availableExams, setAvailableExams] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [examMetaById, setExamMetaById] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'upcoming', 'completed'
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const canViewResultsForExam = (examMeta) => {
    if (!examMeta) return true; // Fallback (will still be enforced on the result page)
    const endDateTime = new Date(`${examMeta.endDate}T${examMeta.endTime}`);
    const now = new Date();
    return !!examMeta.showResultImmediately || now > endDateTime;
  };

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isStudentAuthenticated');
    if (!isAuthenticated) {
      navigate('/student-login');
      return;
    }

    setStudentName(localStorage.getItem('studentName') || 'Student');
    setOrganizationName(localStorage.getItem('studentOrganizationName') || '');
    
    fetchOrganizationData();
    fetchAvailableExams();
    fetchMySubmissions();
  }, [navigate]);

  const fetchOrganizationData = async () => {
    try {
      const studentOrgId = localStorage.getItem('studentOrganizationId');
      if (studentOrgId && studentOrgId !== 'null' && studentOrgId !== '') {
        const docRef = doc(db, 'organizationAdmins', studentOrgId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) {
            setOrgLogo(data.logo);
          }
          if (data.organizationName) {
            setOrganizationName(data.organizationName);
            localStorage.setItem('studentOrganizationName', data.organizationName);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    }
  };

  const fetchAvailableExams = async () => {
    try {
      const studentOrgId = localStorage.getItem('studentOrganizationId');
      const examsList = [];

      // Fetch organization-specific exams if student belongs to an organization
      if (studentOrgId && studentOrgId !== 'null' && studentOrgId !== '') {
        const orgQuery = query(
          collection(db, 'exams'),
          where('organizationId', '==', studentOrgId),
          where('status', '==', 'active')
        );
        const orgSnapshot = await getDocs(orgQuery);
        orgSnapshot.forEach((doc) => {
          examsList.push({ id: doc.id, ...doc.data(), examType: 'My Organization', isOrgExam: true });
        });
      }

      // Fetch ALL public exams (from any organization)
      const publicQuery = query(
        collection(db, 'exams'),
        where('visibility', '==', 'public'),
        where('status', '==', 'active')
      );
      const publicSnapshot = await getDocs(publicQuery);
      publicSnapshot.forEach((doc) => {
        // Avoid duplicates if exam is both org exam and public
        if (!examsList.find(exam => exam.id === doc.id)) {
          examsList.push({ id: doc.id, ...doc.data(), examType: 'Public', isPublicExam: true });
        }
      });

      // Sort: Organization exams first, then public exams
      examsList.sort((a, b) => {
        if (a.isOrgExam && !b.isOrgExam) return -1;
        if (!a.isOrgExam && b.isOrgExam) return 1;
        return 0;
      });

      setAvailableExams(examsList);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const studentId = localStorage.getItem('studentId');
      const submissionsQuery = query(
        collection(db, 'examSubmissions'),
        where('studentId', '==', studentId),
        where('status', '==', 'completed')
      );
      
      const submissionsSnapshot = await getDocs(submissionsQuery);
      const submissionsList = [];
      submissionsSnapshot.forEach((doc) => {
        submissionsList.push({ id: doc.id, ...doc.data() });
      });

      // Sort by submission date (newest first)
      submissionsList.sort((a, b) => {
        return new Date(b.submittedAt) - new Date(a.submittedAt);
      });

      setMySubmissions(submissionsList);

      // Fetch exam metadata for gating result visibility
      const uniqueExamIds = [...new Set(submissionsList.map(s => s.examId).filter(Boolean))];
      const metaMap = {};
      await Promise.all(uniqueExamIds.map(async (examId) => {
        try {
          const examSnap = await getDoc(doc(db, 'exams', examId));
          if (examSnap.exists()) {
            metaMap[examId] = { id: examSnap.id, ...examSnap.data() };
          }
        } catch (e) {
          // Ignore per-exam fetch errors; result page will still enforce access
          console.warn('Failed to fetch exam meta for', examId, e);
        }
      }));
      setExamMetaById(metaMap);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isStudentAuthenticated');
    localStorage.removeItem('studentId');
    localStorage.removeItem('studentUid');
    localStorage.removeItem('studentName');
    localStorage.removeItem('studentEmail');
    localStorage.removeItem('studentOrganizationId');
    localStorage.removeItem('studentOrganizationName');
    navigate('/student-login');
  };

  // Filter exams by status
  const getExamsByStatus = () => {
    const currentDate = new Date();
    const activeExamsList = [];
    const upcomingExamsList = [];

    availableExams.forEach((exam) => {
      const startDateTime = new Date(`${exam.startDate}T${exam.startTime}`);
      const endDateTime = new Date(`${exam.endDate}T${exam.endTime}`);

      if (currentDate >= startDateTime && currentDate <= endDateTime) {
        activeExamsList.push(exam);
      } else if (currentDate < startDateTime) {
        upcomingExamsList.push(exam);
      }
    });

    return { activeExamsList, upcomingExamsList, completedExamsList: mySubmissions };
  };

  const { activeExamsList, upcomingExamsList, completedExamsList } = getExamsByStatus();

  // Check if student has attempted an exam and get attempt info
  const getExamAttemptInfo = (examId) => {
    const attempts = mySubmissions.filter(sub => sub.examId === examId);
    const attemptCount = attempts.length;
    const latestSubmission = attempts.length > 0 ? attempts[0] : null; // Already sorted by newest first
    
    return {
      hasAttempted: attemptCount > 0,
      attemptCount,
      latestSubmission
    };
  };

  const renderExamCard = (exam, isCompleted = false) => {
    if (isCompleted) {
      // Render completed exam (submission)
      const examMeta = examMetaById[exam.examId];
      const resultsAllowed = canViewResultsForExam(examMeta);
      const percentage = exam.percentage || 0;
      const passed = percentage >= 50;
      
      return (
        <div key={exam.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h4 className="text-lg font-bold text-gray-900 mb-2">{exam.examName}</h4>
              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(exam.submittedAt).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {new Date(exam.submittedAt).toLocaleTimeString()}
                </span>
              </div>
            </div>
            
            <div className={`px-4 py-2 rounded-lg font-bold ${
              passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {passed ? '✓ Passed' : '✗ Failed'}
            </div>
          </div>

          {resultsAllowed ? (
            <>
              <div className="grid md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-indigo-600">
                    {exam.totalScore}/{exam.totalMarks}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Score</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-purple-600">
                    {percentage.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Percentage</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">
                    {Object.values(exam.questionResults || {}).filter(q => q.isCorrect).length}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Correct</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-600">
                    {Object.values(exam.questionResults || {}).filter(q => !q.isCorrect).length}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Incorrect</p>
                </div>
              </div>

              <button
                onClick={() => navigate(`/exam-result/${exam.id}`)}
                className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
              >
                View Detailed Results
              </button>
            </>
          ) : (
            <div className="mt-4 p-4 rounded-lg border border-yellow-200 bg-yellow-50">
              <p className="font-semibold text-yellow-900">Results not published yet</p>
              <p className="text-sm text-yellow-800 mt-1">
                Your submission is saved. Results will be visible after the exam ends or when your instructor enables results.
              </p>
              <button
                className="mt-3 w-full px-4 py-3 bg-gray-200 text-gray-500 rounded-lg font-semibold cursor-not-allowed"
                disabled
              >
                View Detailed Results
              </button>
            </div>
          )}
        </div>
      );
    }

    // Render active/upcoming exam
    const currentDate = new Date();
    const startDateTime = new Date(`${exam.startDate}T${exam.startTime}`);
    const endDateTime = new Date(`${exam.endDate}T${exam.endTime}`);
    const resultsAllowed = canViewResultsForExam(exam);
    
    let examStatus = 'upcoming';
    let statusColor = 'bg-yellow-100 text-yellow-800';
    let statusText = 'Upcoming';
    let canStart = false;
    
    if (currentDate >= startDateTime && currentDate <= endDateTime) {
      examStatus = 'live';
      statusColor = 'bg-green-100 text-green-800';
      statusText = 'Live Now';
      canStart = true;
    } else if (currentDate > endDateTime) {
      examStatus = 'ended';
      statusColor = 'bg-gray-100 text-gray-800';
      statusText = 'Ended';
      canStart = false;
    } else {
      canStart = false;
    }

    // Check attempt information
    const attemptInfo = getExamAttemptInfo(exam.id);
    const maxAttempts = exam.attemptLimit === 'unlimited' ? Infinity : 
                       exam.attemptLimit === 'custom' ? parseInt(exam.customAttempts) || 1 :
                       exam.attemptLimit === 'once' ? 1 : parseInt(exam.attemptLimit) || 1;
    const remainingAttempts = maxAttempts - attemptInfo.attemptCount;
    const hasRemainingAttempts = remainingAttempts > 0 || maxAttempts === Infinity;
    
    return (
      <div key={exam.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition">
        {exam.bannerImage && (
          <img 
            src={exam.bannerImage} 
            alt={exam.name} 
            className="w-full h-40 object-cover" 
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="p-6">
          <div className="mb-3">
            <h4 className="text-lg font-bold text-gray-900 mb-2">{exam.name}</h4>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor} flex items-center gap-1`}>
                {examStatus === 'live' && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                )}
                {statusText}
              </span>
              
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                exam.isOrgExam 
                  ? 'bg-indigo-100 text-indigo-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {exam.examType}
              </span>
              
              {exam.showResultImmediately && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  Instant Results
                </span>
              )}
            </div>
          </div>
          
          <div className="space-y-2 mt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Start Date</span>
              <span className="font-semibold text-gray-900">{exam.startDate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Time</span>
              <span className="font-semibold text-gray-900">
                {exam.startTime} - {exam.endTime}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Questions</span>
              <span className="font-semibold text-gray-900">{exam.totalQuestions}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Marks</span>
              <span className="font-semibold text-gray-900">{exam.totalMarks}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Duration</span>
              <span className="font-semibold text-gray-900">{exam.duration} min</span>
            </div>
            {exam.organizationName && !exam.isOrgExam && (
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-600">Created by</span>
                <span className="font-semibold text-gray-900 text-xs">{exam.organizationName}</span>
              </div>
            )}
          </div>
          
          {/* Attempt Info Display */}
          {attemptInfo.hasAttempted && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-800 font-semibold">
                  Attempts: {attemptInfo.attemptCount} / {maxAttempts === Infinity ? '∞' : maxAttempts}
                </span>
                {remainingAttempts > 0 && maxAttempts !== Infinity && (
                  <span className="text-blue-600">
                    {remainingAttempts} left
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 space-y-2">
            {attemptInfo.hasAttempted && !hasRemainingAttempts ? (
              // No remaining attempts - show attended message and view results
              <>
                <button 
                  className="w-full px-4 py-3 bg-gray-200 text-gray-500 rounded-lg font-semibold cursor-not-allowed"
                  disabled
                >
                  Attended - No Attempts Left
                </button>
                {attemptInfo.latestSubmission && resultsAllowed && (
                  <button 
                    onClick={() => navigate(`/exam-result/${attemptInfo.latestSubmission.id}`)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                  >
                    View Results
                  </button>
                )}
                {attemptInfo.latestSubmission && !resultsAllowed && (
                  <button 
                    className="w-full px-4 py-3 bg-gray-200 text-gray-500 rounded-lg font-semibold cursor-not-allowed"
                    disabled
                  >
                    Results Not Published Yet
                  </button>
                )}
              </>
            ) : attemptInfo.hasAttempted && hasRemainingAttempts && canStart ? (
              // Has remaining attempts and exam is active
              <>
                <button 
                  onClick={() => navigate(`/exam-instructions/${exam.id}`)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                >
                  Reattempt Exam
                </button>
                {attemptInfo.latestSubmission && resultsAllowed && (
                  <button 
                    onClick={() => navigate(`/exam-result/${attemptInfo.latestSubmission.id}`)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                  >
                    View Previous Results
                  </button>
                )}
              </>
            ) : (
              // First attempt or not started yet
              <button 
                onClick={() => canStart && navigate(`/exam-instructions/${exam.id}`)}
                disabled={!canStart}
                className={`w-full px-4 py-3 rounded-lg font-semibold transition ${
                  canStart 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg' 
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {canStart ? 'Start Exam' : examStatus === 'upcoming' ? 'Not Started Yet' : 'Exam Ended'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-2 rounded-lg">
                  {orgLogo ? (
                    <img 
                      src={orgLogo} 
                      alt="Organization Logo" 
                      className="h-6 w-6 object-contain"
                    />
                  ) : (
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  )}
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">Student</h2>
                  <p className="text-xs text-gray-500">Portal</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Sidebar Menu */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <button
                onClick={() => setActiveTab('active')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                  activeTab === 'active'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="relative flex items-center justify-center">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {activeExamsList.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {activeExamsList.length}
                    </span>
                  )}
                </span>
                {sidebarOpen && <span className="font-semibold">Active Exams</span>}
              </button>
            </li>
            
            <li>
              <button
                onClick={() => setActiveTab('upcoming')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                  activeTab === 'upcoming'
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="relative flex items-center justify-center">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {upcomingExamsList.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                      {upcomingExamsList.length}
                    </span>
                  )}
                </span>
                {sidebarOpen && <span className="font-semibold">Upcoming</span>}
              </button>
            </li>
            
            <li>
              <button
                onClick={() => setActiveTab('completed')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                  activeTab === 'completed'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="relative flex items-center justify-center">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {completedExamsList.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-indigo-500 text-white text-xs rounded-full flex items-center justify-center">
                      {completedExamsList.length}
                    </span>
                  )}
                </span>
                {sidebarOpen && <span className="font-semibold">Completed</span>}
              </button>
            </li>
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition font-semibold"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white shadow-md sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {activeTab === 'active' && 'Active Exams'}
                  {activeTab === 'upcoming' && 'Upcoming Exams'}
                  {activeTab === 'completed' && 'Completed Exams'}
                </h1>
                <p className="text-gray-600 mt-1">
                  Welcome back, {studentName}
                  {organizationName && <span className="text-indigo-600 font-semibold"> • {organizationName}</span>}
                </p>
              </div>
              
              {/* Stats Badge */}
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl px-6 py-3 border-2 border-indigo-200">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{activeExamsList.length}</p>
                      <p className="text-xs text-gray-600">Active</p>
                    </div>
                    <div className="h-10 w-px bg-gray-300"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">{upcomingExamsList.length}</p>
                      <p className="text-xs text-gray-600">Upcoming</p>
                    </div>
                    <div className="h-10 w-px bg-gray-300"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">{completedExamsList.length}</p>
                      <p className="text-xs text-gray-600">Completed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
                <p className="text-gray-600 mt-4 font-medium">Loading...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Active Exams */}
              {activeTab === 'active' && (
                <div>
                  {activeExamsList.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                      <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Exams</h3>
                      <p className="text-gray-600">There are no exams currently active. Check upcoming exams.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {activeExamsList.map((exam) => renderExamCard(exam))}
                    </div>
                  )}
                </div>
              )}

              {/* Upcoming Exams */}
              {activeTab === 'upcoming' && (
                <div>
                  {upcomingExamsList.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                      <div className="bg-yellow-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="h-10 w-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">No Upcoming Exams</h3>
                      <p className="text-gray-600">There are no scheduled exams at the moment.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {upcomingExamsList.map((exam) => renderExamCard(exam))}
                    </div>
                  )}
                </div>
              )}

              {/* Completed Exams */}
              {activeTab === 'completed' && (
                <div>
                  {completedExamsList.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                      <div className="bg-indigo-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="h-10 w-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">No Completed Exams</h3>
                      <p className="text-gray-600">You haven't completed any exams yet. Start an active exam to see results here.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {completedExamsList.map((exam) => renderExamCard(exam, true))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default StudentDashboard;
